/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: Domain
 * Reason: Read-only knowledge of the run review — the German "Was tun?" guidance per message code
 *         of the commission engine (ProvisionMessageCatalog) and the parser that pulls the agent id
 *         out of a message's `detailedMessage`. Pure functions, no state, no HTTP.
 */
import {ProvisionMessageSeverity} from '../../enum/provision-message-severity.enum';

/**
 * Guidance for ONE message code: what the message means for the operator and what to do next.
 *
 * The texts are written for the Provisionsfachkraft, not for the developer: they name the screen
 * where the fix happens and say whether the run must be recalculated afterwards. They deliberately
 * do NOT restate the message itself — the message text is shown right above the guidance.
 */
export interface CpRunMessageGuidance {
    /** Short German headline of the case, e.g. "Hauptvermittler fehlt". */
    title: string;
    /** German "Was tun?" text — one or two sentences, the concrete next step. */
    action: string;
}

/** Severity ordinal of `ProvisionMessageSeverity.ERROR` — the code the "nur Fehler" filters use. */
export const CP_RUN_SEVERITY_ERROR: number = ProvisionMessageSeverity.ERROR;

/**
 * "Was tun?" per message code (backend: `cp/service/data/ProvisionMessageCatalog.java`).
 *
 * Covers every code the run review is expected to show in practice; a code without an entry falls
 * back to {@link CP_RUN_MESSAGE_GUIDANCE_FALLBACK} instead of rendering nothing.
 */
const CP_RUN_MESSAGE_GUIDANCE: { [code: string]: CpRunMessageGuidance } = {
    'M-001': {
        title: 'Keine gültige Preiszeile',
        action: 'Im Vertrag (cm) die Preiszeile zum Vertragsbeginn prüfen bzw. nachtragen. '
            + 'Danach den Lauf neu berechnen — ohne Preiszeile gibt es keine Provisionsbasis.'
    },
    'M-002': {
        title: 'Hauptvermittler fehlt',
        action: 'Den Vertrag öffnen und im Bereich „Berater“ den Hauptvermittler („abgeschlossen durch“) '
            + 'eintragen. Danach den Lauf neu berechnen.'
    },
    'M-003': {
        title: 'Mehrere Hauptvermittler',
        action: 'Den Vertrag öffnen und die Berater-Zuordnung bereinigen, sodass genau ein Hauptvermittler '
            + 'übrig bleibt. Danach den Lauf neu berechnen.'
    },
    'M-004': {
        title: 'Kein Abrechnungskonto zum Vermittler',
        action: 'Die Vermittler-Id aus der Detailmeldung prüfen: Für genau diesen Vermittler fehlt das '
            + 'Abrechnungskonto. Entweder am Vertrag den richtigen Vermittler eintragen (Vorsicht bei '
            + 'gleichnamigen Vermittlern) oder für diesen Vermittler ein Abrechnungskonto anlegen. '
            + 'Danach den Lauf neu berechnen.'
    },
    'M-005': {
        title: 'Keine Vermittlergruppe zum Stichtag',
        action: 'Im Abrechnungskonto des Vermittlers, Reiter „Gruppen“, eine Gruppenzugehörigkeit anlegen, '
            + 'die den Stichtag abdeckt. Danach den Lauf neu berechnen.'
    },
    'M-007': {
        title: 'Kein Provisionssatz in der Matrix',
        action: 'In der Provisionskonfiguration prüfen, ob die Matrix für diese Gesellschaft/Sparte bzw. '
            + 'diese Vermittlergruppe eine Zeile hat (Beratersicht „Konfiguration“ des Abrechnungskontos '
            + 'zeigt, welcher Satz greift). Konfiguration ergänzen, danach neu berechnen.'
    },
    'M-008': {
        title: 'Keine Provisionsbasis zugeordnet',
        action: 'In der Provisionskonfiguration die Basiswert-Zuordnung für diesen Vertragstyp prüfen und '
            + 'ergänzen. Danach den Lauf neu berechnen.'
    },
    'M-010': {
        title: 'Beitragsfreie Preiszeile',
        action: 'Zur Kenntnis nehmen: Der Vertrag ist zum Beginn beitragsfrei, die Basis ist 0,00. '
            + 'Nur wenn das nicht stimmt, die Preiszeile im Vertrag korrigieren und neu berechnen.'
    },
    'M-012': {
        title: 'Auszahlungssperre (Rücklage negativ)',
        action: 'Der Vermittler wird in diesem Lauf nicht ausgezahlt, weil sein Rücklagensaldo unter 0 liegt. '
            + 'Im Abrechnungskonto den Kontoauszug (Rücklagenkonto) prüfen; ggf. per manueller Buchung '
            + 'ausgleichen.'
    },
    'M-013': {
        title: 'Valuta-Reihenfolge verletzt',
        action: 'Die Auszahlungs-Valuta liegt vor einer bereits ausgezahlten Valuta. Valuta des Laufs prüfen '
            + 'und ggf. einen neuen Lauf mit passender Valuta anlegen.'
    },
    'M-016': {
        title: 'Vertrag nicht mehr in der Auswahl',
        action: 'Zur Kenntnis nehmen: Der Vertrag fällt nicht mehr unter diese Provisionsart, berechnete '
            + 'Sätze wurden bereinigt. Nur prüfen, ob das fachlich gewollt ist (Vertragsdaten, Relevanz).'
    },
    'M-018': {
        title: 'Vermittler inaktiv',
        action: 'Die Auszahlung an diesen Vermittler wurde übersprungen. Status des Vermittlers bzw. des '
            + 'Abrechnungskontos prüfen.'
    },
    'M-019': {
        title: 'Startprüfung fehlgeschlagen',
        action: 'In der Provisionskonfiguration Auszahlungs-Buchungstyp und Buchungszuordnung prüfen. '
            + 'Danach den Schritt erneut starten.'
    },
    'M-025': {
        title: 'Vorwert für Differenz fehlt',
        action: 'Die Basis wurde mit 0,00 angesetzt, weil kein Vorwert ermittelbar war. Preiszeilen des '
            + 'Vertrags bzw. Vorvertrags prüfen.'
    },
    'M-026': {
        title: 'Negativer Betrag auf 0,00 gesetzt',
        action: 'Fachlich klären, warum der Betrag negativ war (z. B. Beitragssenkung im Differenzmodus). '
            + 'Ist eine Rückforderung gewollt, im Abrechnungskonto unter „Provisionssätze“ den Betrag '
            + 'überschreiben.'
    },
    'M-028': {
        title: 'Rolle der Provisionsart nicht besetzt',
        action: 'Für diese Zusatz-Provisionsart ist am Vertrag kein Berater in der nötigen Rolle '
            + 'eingetragen. Nur handeln, wenn die Rolle besetzt sein müsste.'
    },
    'M-029': {
        title: 'Haftungsart aus Überschreibung',
        action: 'Zur Kenntnis nehmen: Die Haftungsart stammt aus einer Überschreibung (Matrixzeile). '
            + 'Nur prüfen, wenn die Haftungsdauer unplausibel ist.'
    },
    'M-035': {
        title: 'Antragsdatum fehlt',
        action: 'Zur Kenntnis nehmen: Als Stichtag wurde ersatzweise der Vertragsbeginn verwendet. '
            + 'Nur wenn ein abweichendes Antragsdatum bekannt ist, im Vertrag nachtragen.'
    },
    'M-038': {
        title: 'Provisionsart am Antragsdatum nicht gültig',
        action: 'Die Provisionsart (z. B. eine Aktion) war am Antragsdatum nicht gültig und wurde '
            + 'übersprungen. Nur prüfen, wenn der Vertrag die Aktion hätte erhalten müssen.'
    },
    'M-039': {
        title: 'Übersteuerung über Schwellenwert',
        action: 'Eine Betragsübersteuerung überschreitet den Schwellenwert der Provisionsart. Den Satz im '
            + 'Abrechnungskonto unter „Provisionssätze“ prüfen und die Übersteuerung bestätigen oder '
            + 'korrigieren.'
    },
    'M-041': {
        title: 'Keine Haftung konfiguriert',
        action: 'In der Provisionskonfiguration die Haftungszuordnung für diesen Vertragstyp prüfen.'
    },
    'M-055': {
        title: 'Null-Provision trotz positiver Basis',
        action: 'Faktor 0 ist so konfiguriert. Prüfen, ob der Berater bzw. die Gruppe in dieser Matrix '
            + 'wirklich keine Provision erhalten soll; falls nicht, Konfiguration durch die '
            + 'Provisionsverantwortliche korrigieren lassen und neu berechnen.'
    }
};

/** Shown for a code the table above does not know yet — never an empty box. */
export const CP_RUN_MESSAGE_GUIDANCE_FALLBACK: CpRunMessageGuidance = {
    title: 'Meldung prüfen',
    action: 'Meldung und Detailmeldung lesen und den betroffenen Vertrag bzw. das Abrechnungskonto prüfen. '
        + 'Nach einer Korrektur den Lauf neu berechnen.'
};

/**
 * Guidance for a message code — never null; unknown codes get the generic fallback.
 *
 * @param messageCode the `M-xxx` code of the message
 */
export function getCpRunMessageGuidance(messageCode: string): CpRunMessageGuidance {
    if (!messageCode) {
        return CP_RUN_MESSAGE_GUIDANCE_FALLBACK;
    }
    return CP_RUN_MESSAGE_GUIDANCE[messageCode.trim()] ?? CP_RUN_MESSAGE_GUIDANCE_FALLBACK;
}

/**
 * The agent id (`am.Agent`) a message names in its `detailedMessage`, or null.
 *
 * The engine writes it as "Vermittler-Id: 9000003133" (M-004 and siblings). This is the ONLY place
 * that number appears — the message carries no agent foreign key — and for duplicate agent names it
 * is the one fact that tells the operator WHICH of two equally named agents is meant.
 *
 * @param detailedMessage the `detailedMessage` of a cp message
 */
export function parseCpRunMessageAgentId(detailedMessage: string): number | null {
    if (!detailedMessage) {
        return null;
    }
    const match = /Vermittler-Id:\s*(\d+)/.exec(detailedMessage);
    if (!match) {
        return null;
    }
    const agentId = Number(match[1]);
    return Number.isFinite(agentId) && agentId > 0 ? agentId : null;
}

/** German label of a message severity ordinal (backend labels: Information / Warnung / Fehler). */
export function getCpRunSeverityLabel(severity: number): string {
    switch (severity) {
        case ProvisionMessageSeverity.ERROR:
            return 'Fehler';
        case ProvisionMessageSeverity.WARNING:
            return 'Warnung';
        default:
            return 'Information';
    }
}

/** PrimeNG tag severity of a message severity ordinal. */
export function getCpRunSeverityTag(severity: number): 'danger' | 'warn' | 'info' {
    switch (severity) {
        case ProvisionMessageSeverity.ERROR:
            return 'danger';
        case ProvisionMessageSeverity.WARNING:
            return 'warn';
        default:
            return 'info';
    }
}
